import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { T, F } from "@/lib/bricks";
import { useAuth } from "@/contexts/AuthContext";
import { SOURCE_LABELS, sourceLabel } from "@/lib/applicantSource";
import { fmtDay } from "@/lib/dates";
import { Page, PageHeader, Stats, Section, List, Row, Meta, Empty, Loading, TextLink, usePalette, humanize } from "@/components/calm/Calm";

const TIER_LABEL = { 1: 'Tier C', 2: 'Tier B', 3: 'Tier A' };

function fmtMoney(cents) {
  return `$${((cents || 0) / 100).toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function greeting(now = new Date()) {
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function total(counts) {
  return Object.values(counts || {}).reduce((a, b) => a + (b || 0), 0);
}

// "3 new · 2 contacted · 1 booked", skipping empty stages.
function stageParts(counts) {
  return Object.entries(counts || {})
    .filter(([, n]) => n > 0)
    .map(([stage, n]) => ({ text: `${n} ${humanize(stage).toLowerCase()}` }));
}

export default function OwnerOverviewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { p } = usePalette();

  useEffect(() => {
    api.get("/owner/overview")
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
    api.get("/admin/signup-sources").then(r => setSources(r.data)).catch(() => {});
  }, []);

  if (loading) return <Loading />;
  if (!data) return null;

  const firstName = user?.name?.split(' ')[0] || 'there';
  const f = data.financials || {};
  const owed = f.total_payouts_owed || 0;

  const pipelines = [
    { key: 'prospectors', title: 'Prospectors', meta: 'Media clients from realtor.ca', counts: data.prospectors_by_stage, to: '/prospectors' },
    { key: 'bookings', title: 'Bookings', meta: 'Paid shoots from the booking page', counts: data.bookings_by_status, to: '/bookings' },
    { key: 'hiring', title: 'Hiring', meta: 'Photographer applications', counts: data.hiring_by_status, to: '/hiring' },
    { key: 'recruit', title: 'Recruiting', meta: 'Agent candidates across all offices', counts: data.recruit_by_stage },
  ];

  const payouts = data.vendor_payouts || [];
  const news = data.recent_newsletter || [];
  const jobs = data.recent_jobs || [];

  return (
    <Page testid="owner-overview-page">
      <PageHeader
        title={`${greeting()}, ${firstName}.`}
        subtitle={<Meta parts={[
          { text: `${data.total_brokerages ?? 0} offices` },
          { text: `${data.total_agents ?? 0} agents` },
          { text: `${data.completed_jobs ?? 0} jobs completed` },
        ]} style={{ fontSize: 13.5 }} />}
      />

      <Stats items={[
        { label: 'Revenue', value: fmtMoney(f.total_revenue) },
        { label: `Active jobs of ${data.total_jobs ?? 0}`, value: String(data.active_jobs ?? 0) },
        { label: 'Payouts owed', value: fmtMoney(owed), attention: owed > 0 },
      ]} />

      <Section title="Revenue, last 30 days" note={<Meta parts={[{ text: `Margin ${fmtMoney(f.total_margin)}` }, { text: `Paid out ${fmtMoney(f.total_payouts_paid)}` }]} />}>
        <div style={{ height: 200, marginLeft: -4 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.revenue_by_day || []} margin={{ top: 4, right: 18, left: 18, bottom: 0 }}>
              <XAxis
                dataKey="date"
                tickFormatter={(d) => fmtDay(d)}
                tick={{ fontFamily: F.mono, fontSize: 10, fill: p.ink4 }}
                axisLine={{ stroke: p.hair }}
                tickLine={false}
                interval={6}
              />
              <Tooltip
                labelFormatter={(d) => fmtDay(d)}
                formatter={(v) => [fmtMoney(v), 'Revenue']}
                contentStyle={{ background: p.bg, border: `1px solid ${p.hair}`, borderRadius: 2, fontFamily: F.display, fontSize: 12 }}
                labelStyle={{ color: p.ink3, fontFamily: F.mono, fontSize: 10 }}
              />
              <Area type="monotone" dataKey="revenue" stroke={T.brick} strokeWidth={2} fill={T.brick} fillOpacity={0.1} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Section>

      <Section title="Pipelines" testid="owner-pipelines">
        <List>
          {pipelines.map(pl => {
            const parts = stageParts(pl.counts);
            return (
              <Row
                key={pl.key}
                testid={`pipeline-${pl.key}`}
                title={pl.title}
                meta={pl.meta}
                next={parts.length ? parts : 'Nothing yet'}
                status={`${total(pl.counts)} total`}
                onClick={pl.to ? () => navigate(pl.to) : undefined}
              />
            );
          })}
        </List>
      </Section>

      <Section title="Vendor payouts" action={<TextLink onClick={() => navigate('/vendors')}>View all</TextLink>}>
        {payouts.length === 0 ? <Empty>No vendor payouts yet.</Empty> : (
          <List>
            {payouts.map(v => (
              <Row
                key={v.vendor_id}
                title={v.name}
                meta={TIER_LABEL[v.tier] || 'Tier C'}
                next={v.amount_owed > 0 ? [{ text: `${fmtMoney(v.amount_owed)} owed`, attention: true }] : 'Nothing owed'}
                status={humanize(v.status)}
                attention={v.status === 'pending' && v.amount_owed > 0}
              />
            ))}
          </List>
        )}
      </Section>

      <Section title="Recent jobs">
        {jobs.length === 0 ? <Empty>No jobs yet. New bookings across every office land here.</Empty> : (
          <List>
            {jobs.map(j => (
              <Row
                key={j.id}
                title={j.address || 'No address'}
                meta={[j.agent_name, j.brokerage].filter(Boolean)}
                next={[{ text: `Booked ${fmtDay(j.created_at)}` }]}
                status={humanize(j.status)}
              />
            ))}
          </List>
        )}
      </Section>

      <Section title="Newsletter" note={`${data.newsletter_count ?? 0} subscribers, ${data.newsletter_founder_count ?? 0} on the founding list`}>
        {news.length === 0 ? <Empty>No subscribers yet.</Empty> : (
          <List>
            {news.map(s => (
              <Row
                key={s.email}
                title={s.email}
                meta={s.source || 'Website'}
                status={s.is_founder ? 'Founding list' : 'Newsletter'}
                next={[{ text: `Joined ${fmtDay(s.created_at)}` }]}
              />
            ))}
          </List>
        )}
      </Section>

      {sources && sources.recent?.length > 0 && (
        <Section title="How sign-ups found us" testid="signup-sources"
          note={<Meta style={{ whiteSpace: 'normal' }} parts={Object.entries(sources.counts || {}).sort((a, b) => b[1] - a[1]).map(([k, n]) => ({ text: `${SOURCE_LABELS[k] || humanize(k)} ${n}` }))} />}>
          <List>
            {sources.recent.slice(0, 6).map(r => (
              <Row
                key={r.id}
                title={r.name || r.email}
                meta={[humanize(r.subscription_type) || 'No plan yet', sourceLabel(r.heard_source, r.heard_source_detail)].filter(Boolean)}
              />
            ))}
          </List>
        </Section>
      )}
    </Page>
  );
}
